/**
 * Service for fetching French city data from the government API
 * API Documentation: https://geo.api.gouv.fr/decoupage-administratif/communes
 */

export interface CityData {
  nom: string;
  code: string;
  codesPostaux: string[];
}

interface CityApiResponse {
  nom: string;
  code: string;
  codesPostaux: string[];
}

const API_BASE_URL = 'https://geo.api.gouv.fr';
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

// Simple cache to avoid redundant API calls
const cache = new Map<string, { data: CityData[]; timestamp: number }>();

/**
 * Fetch city suggestions from the French government API
 * @param query - Search query for city name
 * @param limit - Maximum number of results (default: 5)
 * @returns Promise resolving to array of city data
 */
export async function fetchCitySuggestions(
  query: string,
  limit: number = 5
): Promise<CityData[]> {
  if (!query || query.trim().length < 2) {
    return [];
  }

  const normalizedQuery = query.trim().toLowerCase();
  const cacheKey = `${normalizedQuery}-${limit}`;

  // Check cache first
  const cached = cache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION_MS) {
    return cached.data;
  }

  try {
    const url = `${API_BASE_URL}/communes?nom=${encodeURIComponent(
      query
    )}&fields=nom,code,codesPostaux&limit=${limit}`;

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }

    const data: CityApiResponse[] = await response.json();

    const cities: CityData[] = data.map((city) => ({
      nom: city.nom,
      code: city.code,
      codesPostaux: city.codesPostaux || [],
    }));

    // Cache the results
    cache.set(cacheKey, {
      data: cities,
      timestamp: Date.now(),
    });

    return cities;
  } catch (error) {
    console.error('Error fetching city suggestions:', error);
    throw error;
  }
}

/**
 * Clear the city suggestions cache
 */
export function clearCityCache(): void {
  cache.clear();
}

/**
 * Get cached city suggestions if available
 * @param query - Search query
 * @param limit - Maximum number of results
 * @returns Cached city data or null if not in cache
 */
export function getCachedCitySuggestions(
  query: string,
  limit: number = 5
): CityData[] | null {
  const normalizedQuery = query.trim().toLowerCase();
  const cacheKey = `${normalizedQuery}-${limit}`;
  const cached = cache.get(cacheKey);

  if (cached && Date.now() - cached.timestamp < CACHE_DURATION_MS) {
    return cached.data;
  }

  return null;
}

